import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PoweredByFooter from "../components/PoweredByFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  ChevronLeft, Send, MessageSquare, Users, Clock, 
  Archive, Trash2, RefreshCw, Search, CalendarClock,
  Repeat, Bell, AlertTriangle, Check, X, History, Eye, Copy, Edit
} from "lucide-react";

export default function Beskeder({ user, selectedEvent }) {
  const navigate = useNavigate();
  const eventId = selectedEvent?.id;
  const token = localStorage.getItem("token");
  const isAdmin = user?.role === 'admin';
  
  const [messages, setMessages] = useState([]);
  const [roles, setRoles] = useState([]);
  const [myRole, setMyRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox'); // 'inbox', 'scheduled', 'history'
  
  // Send besked form
  const [showSendForm, setShowSendForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null); // Til redigering/duplikering
  const [newMessage, setNewMessage] = useState({ 
    subject: '', 
    content: '', 
    recipient_role_ids: [],
    scheduled_time: '',
    repeat_type: '',
    repeat_interval: 1,
    repeat_unit: 'days', // 'minutes', 'hours', 'days'
    repeat_until: '',
    notification_type: 'normal'
  });
  const [sending, setSending] = useState(false);
  
  // Planlagte beskeder
  const [scheduledMessages, setScheduledMessages] = useState([]);
  
  // Notifikationshistorik
  const [notificationHistory, setNotificationHistory] = useState([]);
  
  // Hent roller bruger kan sende til
  const [canSendTo, setCanSendTo] = useState([]);
  
  // Check om bruger er leder
  const [isLeader, setIsLeader] = useState(false);

  const loadData = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    
    try {
      // Hent beskeder
      const messagesRes = await axios.get(
        `${API}/events/${eventId}/messages${showArchive ? '/archive' : ''}`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setMessages(messagesRes.data);
      
      // Hent roller
      const rolesRes = await axios.get(`${API}/events/${eventId}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(rolesRes.data);
      
      // Find brugerens rolle og can_send_to
      if (!isAdmin) {
        // Hent brugerens frivillig-info
        const volunteerRes = await axios.get(`${API}/events/${eventId}/volunteers/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const myVolunteer = volunteerRes.data.find(v => 
          v.email?.toLowerCase() === user?.email?.toLowerCase()
        );
        
        if (myVolunteer) {
          const myRoleData = rolesRes.data.find(r => r.name === myVolunteer.role);
          setMyRole(myRoleData);
          setCanSendTo(myRoleData?.can_send_to || []);
          setIsLeader(myVolunteer.is_leader || false);
        }
      } else {
        // Admin kan sende til alle
        setCanSendTo(rolesRes.data.map(r => r.id));
        setIsLeader(true);
      }
    } catch (error) {
      console.error("Fejl ved hentning af data:", error);
      if (error.response?.status !== 403) {
        toast.error("Kunne ikke hente beskeder");
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, token, showArchive, isAdmin, user?.email]);

  const loadScheduledMessages = useCallback(async () => {
    if (!eventId || (!isAdmin && !isLeader)) return;
    
    try {
      const res = await axios.get(`${API}/events/${eventId}/scheduled-messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledMessages(res.data);
    } catch (error) {
      console.error("Fejl ved hentning af planlagte beskeder:", error);
    }
  }, [eventId, token, isAdmin, isLeader]);

  const loadNotificationHistory = useCallback(async () => {
    if (!eventId) return;
    
    try {
      const res = await axios.get(`${API}/events/${eventId}/notifications/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationHistory(res.data);
    } catch (error) {
      console.error("Fejl ved hentning af notifikationshistorik:", error);
    }
  }, [eventId, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'scheduled') {
      loadScheduledMessages();
    } else if (activeTab === 'history') {
      loadNotificationHistory();
    }
  }, [activeTab, loadScheduledMessages, loadNotificationHistory]);

  // Filtrer beskeder
  const filteredMessages = messages.filter(msg => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      msg.subject?.toLowerCase().includes(term) ||
      msg.content?.toLowerCase().includes(term) ||
      msg.sender_name?.toLowerCase().includes(term) ||
      msg.sender_role?.toLowerCase().includes(term)
    );
  });

  const sendMessage = async () => {
    if (!newMessage.content || newMessage.recipient_role_ids.length === 0) {
      toast.error("Udfyld besked og vælg mindst én modtager");
      return;
    }
    
    setSending(true);
    try {
      const payload = {
        ...newMessage,
        repeat_type: newMessage.repeat_type || null,
        repeat_interval: newMessage.repeat_type ? newMessage.repeat_interval : null,
        repeat_until: newMessage.repeat_type && newMessage.repeat_until ? newMessage.repeat_until : null,
        scheduled_time: newMessage.scheduled_time || null
      };
      
      const res = await axios.post(`${API}/events/${eventId}/messages`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (newMessage.scheduled_time) {
        toast.success("Besked planlagt!");
        loadScheduledMessages();
      } else {
        toast.success("Besked sendt!");
      }
      
      setNewMessage({ 
        subject: '', 
        content: '', 
        recipient_role_ids: [],
        scheduled_time: '',
        repeat_type: '',
        repeat_interval: 1,
        repeat_until: '',
        notification_type: 'normal'
      });
      setShowSendForm(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke sende besked");
    } finally {
      setSending(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!confirm("Er du sikker på du vil slette denne besked?")) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Besked slettet");
      loadData();
    } catch (error) {
      toast.error("Kunne ikke slette besked");
    }
  };

  const deleteScheduledMessage = async (messageId) => {
    if (!confirm("Er du sikker på du vil annullere denne planlagte besked?")) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}/scheduled-messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Planlagt besked annulleret");
      loadScheduledMessages();
    } catch (error) {
      toast.error("Kunne ikke annullere planlagt besked");
    }
  };
  
  // Redigér en planlagt besked
  const editScheduledMessage = (msg) => {
    setEditingMessage(msg);
    setNewMessage({
      subject: msg.subject || '',
      content: msg.content || '',
      recipient_role_ids: msg.recipient_role_ids || [],
      scheduled_time: msg.scheduled_time ? new Date(msg.scheduled_time).toISOString().slice(0, 16) : '',
      repeat_type: msg.repeat_type || '',
      repeat_interval: msg.repeat_interval || 1,
      repeat_unit: msg.repeat_unit || 'days',
      repeat_until: msg.repeat_until ? new Date(msg.repeat_until).toISOString().slice(0, 16) : '',
      notification_type: msg.notification_type || 'normal'
    });
    setShowSendForm(true);
  };
  
  // Duplikér en planlagt besked (åbn som ny med forudfyldte felter)
  const duplicateScheduledMessage = (msg) => {
    setEditingMessage(null); // Ikke redigering, men ny besked
    setNewMessage({
      subject: msg.subject || '',
      content: msg.content || '',
      recipient_role_ids: msg.recipient_role_ids || [],
      scheduled_time: '', // Tom - brugeren skal vælge ny tid
      repeat_type: msg.repeat_type || '',
      repeat_interval: msg.repeat_interval || 1,
      repeat_unit: msg.repeat_unit || 'days',
      repeat_until: '', // Tom - brugeren skal vælge ny slutdato
      notification_type: msg.notification_type || 'normal'
    });
    setShowSendForm(true);
    toast.info("Besked duplikeret - vælg ny dato/tid");
  };

  // Kopier en allerede afsendt besked (åbn som ny med forudfyldte felter)
  const duplicateSentMessage = (msg) => {
    setEditingMessage(null);
    setNewMessage({
      subject: msg.subject || '',
      content: msg.content || '',
      recipient_role_ids: msg.recipient_role_ids || [],
      scheduled_time: '',
      repeat_type: '',
      repeat_interval: 1,
      repeat_unit: 'days',
      repeat_until: '',
      notification_type: msg.notification_type || 'normal'
    });
    setShowSendForm(true);
    toast.info("Besked kopieret - klar til at sende igen");
  };
  
  // Reset form
  const resetForm = () => {
    setEditingMessage(null);
    setNewMessage({
      subject: '',
      content: '',
      recipient_role_ids: [],
      scheduled_time: '',
      repeat_type: '',
      repeat_interval: 1,
      repeat_unit: 'days',
      repeat_until: '',
      notification_type: 'normal'
    });
    setShowSendForm(false);
  };

  const toggleRecipient = (roleId) => {
    if (newMessage.recipient_role_ids.includes(roleId)) {
      setNewMessage({
        ...newMessage,
        recipient_role_ids: newMessage.recipient_role_ids.filter(id => id !== roleId)
      });
    } else {
      setNewMessage({
        ...newMessage,
        recipient_role_ids: [...newMessage.recipient_role_ids, roleId]
      });
    }
  };

  // Roller bruger kan sende til
  const sendableRoles = roles.filter(r => canSendTo.includes(r.id));
  
  // Kan bruger planlægge beskeder?
  const canSchedule = isAdmin || isLeader;

  const getNotificationTypeIcon = (type) => {
    switch(type) {
      case 'urgent': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'important': return <Bell className="h-4 w-4 text-yellow-500" />;
      default: return <MessageSquare className="h-4 w-4 text-blue-400" />;
    }
  };

  const getNotificationTypeBadge = (type) => {
    switch(type) {
      case 'urgent': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'important': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="beskeder-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Beskeder
              </h1>
              <p className="text-xs text-muted-foreground">{selectedEvent?.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant={showArchive ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchive(!showArchive)}
              >
                <Archive className="h-4 w-4 mr-2" />
                {showArchive ? "Vis mine" : "Arkiv"}
              </Button>
            )}
            
            <Button onClick={() => setShowSendForm(true)} disabled={sendableRoles.length === 0}>
              <Send className="h-4 w-4 mr-2" />
              Ny besked
            </Button>
            
            <Button variant="ghost" size="sm" onClick={loadData}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Tabs - sticky under header */}
        <div className="container mx-auto px-4 pb-4">
          <div className="flex gap-2">
            <Button
              variant={activeTab === 'inbox' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('inbox')}
              data-testid="tab-inbox"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Indbakke
            </Button>
            {canSchedule && (
              <Button
                variant={activeTab === 'scheduled' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab('scheduled')}
                data-testid="tab-scheduled"
              >
                <CalendarClock className="h-4 w-4 mr-2" />
                Planlagte ({scheduledMessages.filter(m => m.status === 'pending').length})
              </Button>
            )}
            <Button
              variant={activeTab === 'history' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('history')}
              data-testid="tab-history"
            >
              <History className="h-4 w-4 mr-2" />
              Historik
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">

        {/* Send besked formular */}
        {showSendForm && (
          <div className="mb-6 p-6 bg-card border border-primary/50 rounded-lg">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send ny besked
            </h2>
            
            <div className="space-y-4">
              {/* Modtagere */}
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Vælg modtagere ({newMessage.recipient_role_ids.length} valgt)
                </Label>
                {sendableRoles.length === 0 ? (
                  <p className="text-sm text-yellow-500">
                    Du har ikke tilladelse til at sende beskeder til nogen roller.
                    Kontakt admin for at få tildelt besked-rettigheder.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {sendableRoles.map(role => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => toggleRecipient(role.id)}
                        className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                          newMessage.recipient_role_ids.includes(role.id)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted hover:bg-muted/80'
                        }`}
                      >
                        {role.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Notifikationstype */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Vigtighed</Label>
                <div className="flex gap-2">
                  {[
                    { value: 'normal', label: 'Normal', icon: MessageSquare, color: 'blue' },
                    { value: 'important', label: 'Vigtig', icon: Bell, color: 'yellow' },
                    { value: 'urgent', label: 'Akut', icon: AlertTriangle, color: 'red' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setNewMessage({ ...newMessage, notification_type: type.value })}
                      className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-all border ${
                        newMessage.notification_type === type.value
                          ? `bg-${type.color}-500/20 border-${type.color}-500/50 text-${type.color}-400`
                          : 'bg-muted border-border hover:bg-muted/80'
                      }`}
                    >
                      <type.icon className={`h-4 w-4 ${newMessage.notification_type === type.value ? `text-${type.color}-500` : ''}`} />
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Emne */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Emne (valgfrit)</Label>
                <Input
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                  placeholder="Kort beskrivelse af beskeden"
                />
              </div>
              
              {/* Besked */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Besked *</Label>
                <textarea
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                  placeholder="Skriv din besked her..."
                  className="w-full h-32 px-3 py-2 bg-zinc-800 border border-border rounded-md text-white resize-none"
                />
              </div>
              
              {/* Tidsplanlægning (kun admin/ledere) */}
              {canSchedule && (
                <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg space-y-4">
                  <div className="flex items-center gap-2">
                    <CalendarClock className="h-5 w-5 text-indigo-400" />
                    <Label className="text-sm font-medium">Tidsplanlægning (valgfrit)</Label>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Send på tidspunkt
                      </Label>
                      <Input
                        type="datetime-local"
                        value={newMessage.scheduled_time}
                        onChange={(e) => setNewMessage({ ...newMessage, scheduled_time: e.target.value })}
                        min="2024-01-01T00:00"
                        max="2099-12-31T23:59"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Lad feltet være tomt for at sende nu
                      </p>
                    </div>
                    
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Gentag
                      </Label>
                      <select
                        value={newMessage.repeat_type}
                        onChange={(e) => setNewMessage({ ...newMessage, repeat_type: e.target.value })}
                        className="w-full h-10 px-3 bg-zinc-800 border border-border rounded-md text-white"
                      >
                        <option value="">Ingen gentagelse</option>
                        <option value="custom">Brugerdefineret interval</option>
                      </select>
                    </div>
                  </div>
                  
                  {newMessage.repeat_type === 'custom' && (
                    <div className="space-y-4 bg-muted/30 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            Gentag hver
                          </Label>
                          <Input
                            type="number"
                            min="1"
                            value={newMessage.repeat_interval}
                            onChange={(e) => setNewMessage({ ...newMessage, repeat_interval: parseInt(e.target.value) || 1 })}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            Enhed
                          </Label>
                          <select
                            value={newMessage.repeat_unit}
                            onChange={(e) => setNewMessage({ ...newMessage, repeat_unit: e.target.value })}
                            className="w-full h-10 px-3 bg-zinc-800 border border-border rounded-md text-white"
                          >
                            <option value="minutes">Minut(ter)</option>
                            <option value="hours">Time(r)</option>
                            <option value="days">Dag(e)</option>
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-1 block">
                            Stop gentagelse efter
                          </Label>
                          <Input
                            type="datetime-local"
                            value={newMessage.repeat_until}
                            onChange={(e) => setNewMessage({ ...newMessage, repeat_until: e.target.value })}
                            min="2024-01-01T00:00"
                            max="2099-12-31T23:59"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Beskeden sendes hver {newMessage.repeat_interval} {
                          newMessage.repeat_unit === 'minutes' ? 'minut' + (newMessage.repeat_interval > 1 ? 'ter' : '') :
                          newMessage.repeat_unit === 'hours' ? 'time' + (newMessage.repeat_interval > 1 ? 'r' : '') :
                          'dag' + (newMessage.repeat_interval > 1 ? 'e' : '')
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-2">
                <Button onClick={sendMessage} disabled={sending}>
                  {sending ? 'Sender...' : 
                   editingMessage ? 'Opdater besked' :
                   newMessage.scheduled_time ? 'Planlæg besked' : 'Send besked'}
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  Annuller
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* INDBAKKE TAB */}
        {activeTab === 'inbox' && (
          <>
            {/* Søgefelt */}
            <div className="flex gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Søg i beskeder..."
                  className="pl-10"
                />
              </div>
            </div>

            {/* Beskeder liste */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="mt-4 text-muted-foreground">Indlæser beskeder...</p>
              </div>
            ) : filteredMessages.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchTerm ? 'Ingen beskeder matcher din søgning' : 'Ingen beskeder endnu'}
                </p>
                {!searchTerm && sendableRoles.length > 0 && (
                  <Button className="mt-4" onClick={() => setShowSendForm(true)}>
                    <Send className="h-4 w-4 mr-2" />
                    Send den første besked
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMessages.map(msg => (
                  <div 
                    key={msg.id} 
                    className={`bg-card border rounded-lg p-4 hover:border-primary/30 transition-colors ${
                      msg.notification_type === 'urgent' ? 'border-red-500/50' :
                      msg.notification_type === 'important' ? 'border-yellow-500/50' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          msg.notification_type === 'urgent' ? 'bg-red-500/20' :
                          msg.notification_type === 'important' ? 'bg-yellow-500/20' : 'bg-primary/20'
                        }`}>
                          {getNotificationTypeIcon(msg.notification_type)}
                        </div>
                        <div>
                          <p className="font-medium">
                            {msg.sender_name}
                            <span className="text-sm text-muted-foreground ml-2">
                              ({msg.sender_role || 'Admin'})
                            </span>
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(msg.created_at).toLocaleString('da-DK')}
                            {msg.is_scheduled && (
                              <span className="ml-2 text-indigo-400">
                                <CalendarClock className="h-3 w-3 inline mr-1" />
                                Planlagt
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {/* Modtagere */}
                        <div className="flex flex-wrap gap-1">
                          {(msg.recipient_names || []).map((name, idx) => (
                            <span 
                              key={idx}
                              className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded"
                            >
                              → {name}
                            </span>
                          ))}
                        </div>
                        
                        {sendableRoles.length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => duplicateSentMessage(msg)}
                            className="text-green-400 hover:bg-green-500/10"
                            title="Kopier besked"
                            data-testid={`copy-sent-msg-${msg.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}

                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMessage(msg.id)}
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {msg.subject && (
                      <h3 className="font-bold mb-2">{msg.subject}</h3>
                    )}
                    
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* PLANLAGTE BESKEDER TAB */}
        {activeTab === 'scheduled' && canSchedule && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-indigo-400" />
                Planlagte beskeder
              </h2>
              <Button variant="outline" size="sm" onClick={loadScheduledMessages}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Opdater
              </Button>
            </div>

            {scheduledMessages.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <CalendarClock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Ingen planlagte beskeder</p>
                <Button className="mt-4" onClick={() => setShowSendForm(true)}>
                  <Send className="h-4 w-4 mr-2" />
                  Opret planlagt besked
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {scheduledMessages.map(msg => (
                  <div 
                    key={msg.id} 
                    className={`bg-card border rounded-lg p-4 ${
                      msg.status === 'pending' ? 'border-indigo-500/50' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            msg.status === 'pending' 
                              ? 'bg-indigo-500/20 text-indigo-400' 
                              : 'bg-green-500/20 text-green-400'
                          }`}>
                            {msg.status === 'pending' ? 'Afventer' : 'Sendt'}
                          </span>
                          
                          {msg.repeat_type && (
                            <span className="px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400 flex items-center gap-1">
                              <Repeat className="h-3 w-3" />
                              {msg.repeat_type === 'hourly' 
                                ? `Hver ${msg.repeat_interval} time${msg.repeat_interval > 1 ? 'r' : ''}`
                                : `Hver ${msg.repeat_interval} dag${msg.repeat_interval > 1 ? 'e' : ''}`
                              }
                            </span>
                          )}
                          
                          <span className={`px-2 py-0.5 rounded text-xs border ${getNotificationTypeBadge(msg.notification_type)}`}>
                            {msg.notification_type === 'urgent' ? 'Akut' : 
                             msg.notification_type === 'important' ? 'Vigtig' : 'Normal'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          Sendes: {new Date(msg.scheduled_time).toLocaleString('da-DK')}
                        </p>
                        
                        {msg.subject && (
                          <h4 className="font-medium mb-1">{msg.subject}</h4>
                        )}
                        
                        <p className="text-sm text-muted-foreground line-clamp-2">{msg.content}</p>
                        
                        <div className="flex flex-wrap gap-1 mt-2">
                          <span className="text-xs text-muted-foreground">Til:</span>
                          {(msg.recipient_names || []).map((name, idx) => (
                            <span key={idx} className="text-xs px-2 py-0.5 bg-muted rounded">
                              {name}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {msg.status === 'pending' && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => editScheduledMessage(msg)}
                            className="text-blue-400 hover:bg-blue-500/10"
                            title="Redigér besked"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => duplicateScheduledMessage(msg)}
                            className="text-green-400 hover:bg-green-500/10"
                            title="Duplikér besked"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteScheduledMessage(msg.id)}
                            className="text-destructive hover:bg-destructive/10"
                            title="Slet besked"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HISTORIK TAB */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <History className="h-5 w-5 text-amber-400" />
                Notifikationshistorik
              </h2>
              <Button variant="outline" size="sm" onClick={loadNotificationHistory}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Opdater
              </Button>
            </div>

            {notificationHistory.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Ingen notifikationer i historikken</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notificationHistory.map(notif => (
                  <div 
                    key={notif.id} 
                    className={`bg-card border border-border rounded-lg p-3 ${
                      notif.is_dismissed ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getNotificationTypeIcon(notif.type)}
                        <div>
                          <p className="text-sm font-medium">
                            {notif.subject}
                            {notif.is_dismissed && (
                              <span className="ml-2 text-xs text-muted-foreground">(Læst)</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Fra: {notif.sender_name} • {new Date(notif.created_at).toLocaleString('da-DK')}
                          </p>
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs border ${getNotificationTypeBadge(notif.type)}`}>
                        {notif.type === 'urgent' ? 'Akut' : 
                         notif.type === 'important' ? 'Vigtig' : 'Normal'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{notif.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <PoweredByFooter />
      </main>
    </div>
  );
}
